import React, { useEffect, useMemo } from 'react';
import { Package, ShoppingCart, Clock3, BarChart2 } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { KpiMetric } from '../../../store/stockDashboardStore';
import { useInventoryStore } from '../../../store/inventoryStore';
import { useStockDashboardStore } from '../../../store/stockDashboardStore';
import { useStockRequestStore } from '../../../store/stockRequestStore';
import { countPendingRequests } from '../../../store/stockRequestAssignment';
import { useWidgetSize } from './useWidgetSize';

interface KpiWidgetProps {
  title: string;
  accentColor: string;
  bgTint: string;
  kpiMetric?: KpiMetric;
  isDark: boolean;
}

const ICONS: Record<KpiMetric, LucideIcon> = {
  total_items:      Package,
  checked_out:      ShoppingCart,
  pending_requests: Clock3,
  inventory_value:  BarChart2,
};

const KpiWidget: React.FC<KpiWidgetProps> = ({
  title,
  accentColor,
  bgTint,
  kpiMetric = 'total_items',
  isDark,
}) => {
  const { ref, isXS, isSM, isLG, isXL } = useWidgetSize();
  const { stockUnits } = useInventoryStore();
  const { requests, fetchRequests } = useStockRequestStore();
  const { globalScopeWarehouseId } = useStockDashboardStore();

  useEffect(() => {
    if (kpiMetric === 'pending_requests') fetchRequests();
  }, [kpiMetric, fetchRequests]);

  const value = useMemo(() => {
    const units = globalScopeWarehouseId
      ? stockUnits.filter(u => u.warehouseId === globalScopeWarehouseId)
      : stockUnits;

    switch (kpiMetric) {
      case 'total_items':
        return units.filter(u => u.status !== 'retired').length;
      case 'checked_out':
        return units.filter(u => u.status === 'checked out').length;
      case 'pending_requests':
        return countPendingRequests(requests, globalScopeWarehouseId);
      case 'inventory_value':
        return units.filter(u => u.status === 'in stock').length + ' in stock';
      default:
        return 0;
    }
  }, [stockUnits, requests, globalScopeWarehouseId, kpiMetric]);

  const Icon = ICONS[kpiMetric];

  const hasAccent = !!accentColor;
  const iconBg    = hasAccent ? `${accentColor}22` : (isDark ? '#2b3140' : '#f3f4f6');
  const iconColor = hasAccent ? accentColor        : (isDark ? '#9ca3af' : '#6b7280');

  const iconSize   = isXS ? 20 : isSM ? 22 : isLG ? 20 : 18;
  const iconBoxCls = isXS ? 'w-8 h-8' : isSM ? 'w-9 h-9' : isLG ? 'w-10 h-10' : 'w-9 h-9';

  const valueCls = isXS
    ? 'text-xl font-semibold leading-tight'
    : isSM
      ? 'text-2xl font-semibold leading-tight'
      : isLG
        ? (isXL ? 'text-4xl font-bold leading-tight' : 'text-3xl font-bold leading-tight')
        : 'text-2xl font-semibold leading-tight';

  const labelCls = isXS ? 'text-[10px]' : isSM ? 'text-xs' : 'text-sm';
  const padCls   = isXS ? 'px-2 py-2' : isSM ? 'px-3 py-2.5' : 'px-4 py-3';
  const gapCls   = isXS ? 'gap-2' : 'gap-3';
  const layoutCls = isXS ? 'flex-col items-start' : 'items-center';

  return (
    <div
      ref={ref}
      className={`h-full flex ${layoutCls} ${gapCls} ${padCls} rounded border border-l-4 overflow-hidden select-none`}
      style={{
        backgroundColor: bgTint || (isDark ? '#20242b' : '#ffffff'),
        borderColor: isDark ? '#343a43' : '#e5e7eb',
        borderLeftColor: accentColor || (isDark ? '#343a43' : '#e5e7eb'),
      }}
    >
      <div
        className={`${iconBoxCls} rounded flex items-center justify-center flex-shrink-0`}
        style={{ backgroundColor: iconBg, color: iconColor }}
      >
        <Icon size={iconSize} />
      </div>
      <div className="min-w-0">
        {!isXS && (
          <p className={`${labelCls} truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{title}</p>
        )}
        <p className={`${valueCls} ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>{value}</p>
        {isXS && (
          <p className={`text-[9px] truncate mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{title}</p>
        )}
      </div>
    </div>
  );
};

export default KpiWidget;
